import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GppGoodIcon from "@mui/icons-material/GppGood";
import { useState } from "react";

const AnexarLicencaModal = ({ open, onClose, onSubmit, processo, showCertificate = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [certificadoPfxFile, setCertificadoPfxFile] = useState(null);
  const [certificadoSenha, setCertificadoSenha] = useState("");

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
      setShowConfirmation(false);
      setErro(null);
    } else if (file) {
      alert("Por favor, selecione apenas arquivos PDF.");
      event.target.value = "";
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSelectedFile(null);
    setShowConfirmation(false);
    setErro(null);
    setCertificadoPfxFile(null);
    setCertificadoSenha("");
    onClose();
  };

  const handleContinue = () => {
    if (!selectedFile) {
      alert("Por favor, selecione um arquivo PDF antes de continuar.");
      return;
    }
    if (showCertificate) {
      if (!certificadoPfxFile) {
        setErro("É obrigatório carregar o certificado digital (.pfx) para emitir a licença.");
        return;
      }
      if (!certificadoSenha.trim()) {
        setErro("É obrigatória a senha do certificado digital.");
        return;
      }
    }
    setErro(null);
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setErro(null);
    try {
      await onSubmit(selectedFile, processo, certificadoPfxFile || undefined, certificadoSenha || undefined);
      setSelectedFile(null);
      setShowConfirmation(false);
      setCertificadoPfxFile(null);
      setCertificadoSenha("");
      onClose();
    } catch (error) {
      setErro(error.message || "Erro ao anexar licença. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSelection = () => {
    setErro(null);
    setShowConfirmation(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#2196F3", color: "white" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Anexar Licença</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {!showConfirmation ? (
          <Box>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
              Selecione o arquivo PDF da licença para o processo:{" "}
              <strong>{processo?.nomeTipoLicenca || "N/A"}</strong>
            </Typography>

            <Box
              sx={{
                border: "2px dashed #2196F3",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                bgcolor: "rgba(33, 150, 243, 0.05)",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "rgba(33, 150, 243, 0.1)",
                  borderColor: "#1976D2",
                },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
              component="label"
            >
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <UploadFileIcon sx={{ fontSize: 56, color: "#2196F3" }} />
              <Typography variant="body1" fontWeight={500}>
                Clique para selecionar o arquivo PDF
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Apenas arquivos PDF são permitidos
              </Typography>
            </Box>

            {selectedFile && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Arquivo selecionado:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Alert>
            )}

            {showCertificate && (
              <Box sx={{ mt: 3, p: 2, border: "1px solid #c8e6c9", borderRadius: 1, bgcolor: "#f1f8e9" }}>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <GppGoodIcon sx={{ color: "#388e3c", fontSize: 20 }} />
                  <Typography variant="subtitle2" color="#2e7d32">
                    Assinatura Digital com Certificado ICP-Brasil
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Carregue seu certificado .pfx e informe a senha. A licença será assinada digitalmente antes de ser emitida.
                </Typography>
                <Button
                  component="label"
                  variant={certificadoPfxFile ? "contained" : "outlined"}
                  color={certificadoPfxFile ? "success" : "inherit"}
                  size="small"
                  startIcon={<GppGoodIcon />}
                  sx={{ mb: 1.5 }}
                >
                  {certificadoPfxFile ? certificadoPfxFile.name : "Selecionar certificado .pfx"}
                  <input
                    type="file"
                    accept=".pfx,.p12"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files[0];
                      if (f) { setCertificadoPfxFile(f); setErro(null); }
                      e.target.value = "";
                    }}
                  />
                </Button>
                <TextField
                  label="Senha do certificado"
                  type="password"
                  size="small"
                  fullWidth
                  required
                  value={certificadoSenha}
                  onChange={(e) => setCertificadoSenha(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Senha do arquivo .pfx"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GppGoodIcon color="success" sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 3,
                p: 2.5,
                bgcolor: "#FFF3E0",
                borderRadius: 1,
              }}
            >
              <WarningAmberIcon
                sx={{ color: "#FF9800", mr: 1.5, fontSize: 32, mt: 0.5 }}
              />
              <Box>
                <Typography variant="h6" color="#E65100" gutterBottom>
                  Atenção! Esta ação não pode ser desfeita
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ao confirmar o envio da licença, o status do processo será
                  alterado para <strong>"Licença Emitida"</strong> e esta ação
                  não poderá ser revertida.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Processo:</strong> {processo?.nomeTipoLicenca || "N/A"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Arquivo:</strong> {selectedFile?.name}
              </Typography>
              <Typography variant="body2">
                <strong>Tamanho:</strong>{" "}
                {(selectedFile?.size / 1024).toFixed(2)} KB
              </Typography>
              {showCertificate && certificadoPfxFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "#2e7d32", display: "flex", alignItems: "center", gap: 0.5 }}>
                  <GppGoodIcon sx={{ fontSize: 16 }} />
                  <strong>Assinatura digital:</strong> {certificadoPfxFile.name}
                </Typography>
              )}
            </Box>

            <DialogContentText sx={{ mt: 3, color: "text.primary" }}>
              <strong>Você tem certeza de que deseja continuar?</strong>
            </DialogContentText>
          </Box>
        )}
      </DialogContent>

      {erro && (
        <Box sx={{ px: 3, pt: 1 }}>
          <Alert severity="error" onClose={() => setErro(null)}>
            {erro}
          </Alert>
        </Box>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!showConfirmation ? (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit" disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleContinue}
              variant="contained"
              disabled={!selectedFile || loading}
              startIcon={<UploadFileIcon />}
            >
              Continuar
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleBackToSelection}
              variant="outlined"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="warning"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <WarningAmberIcon />}
            >
              {loading ? "Enviando..." : "Sim, Anexar Licença"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnexarLicencaModal;
